import { earn, money, spend, type LifeState } from './life.ts';
import { hereCountry, localPrice } from './character.ts';
import { Rng } from './rng.ts';

/**
 * The betting parlour, in every city (made-up names: real bookmakers are businesses): real money in, real money
 * out. The virtual league (three matches an hour, priced with a bookmaker's margin, played out on the shop's
 * screen), a machine (fruit machines; pachislot in Tokyo) and, in Shanghai, scratch cards from the lottery shop.
 * The house edge is the real one: over time you lose, some nights you don't. Nothing is hidden: the odds are on
 * the board, and staking a big part of what you have asks you first.
 */

export const PARLOURS: Record<string, { name: string; kind: string }> = {
  nigeria: { name: 'Lucky Odds, Allen Avenue', kind: 'betting shop' },
  france: { name: 'Le Galop, Bastille', kind: 'PMU bar' },
  usa: { name: 'Hudson Line Sportsbook, Midtown', kind: 'sportsbook' },
  china: { name: 'Lucky Star Lottery Shop, Huaihai Road', kind: 'lottery shop' },
  japan: { name: 'Galaxy Pachislot, Shibuya', kind: 'pachislot parlour' },
  italy: { name: 'Sala Scommesse Fortuna, Florence', kind: 'betting shop' },
};
export const parlourOf = (s: LifeState) => PARLOURS[hereCountry(s)] ?? PARLOURS.nigeria;

/** made-up clubs, by country (strength 0..1) */
const CLUBS: Record<string, [string, string, number][]> = {
  nigeria: [['LAG', 'Lagos Rovers', 0.72], ['ABJ', 'Abuja City', 0.66], ['PHC', 'Port Harcourt United', 0.6], ['IBD', 'Ibadan Stars', 0.55], ['KAN', 'Kano Pillars FC', 0.62], ['ENU', 'Enugu Rangers XI', 0.5]],
  france: [['PSE', 'Paris Seine FC', 0.74], ['MAR', 'Olympique Calanques', 0.68], ['LYO', 'Lyon Confluence', 0.63], ['LIL', 'Lille Métropole', 0.58], ['NAN', 'Nantes Atlantique', 0.5], ['BOR', 'Bordeaux Gironde', 0.52]],
  usa: [['NYH', 'New York Harbor FC', 0.7], ['CHI', 'Chicago Lakeshore', 0.64], ['SFB', 'San Francisco Bay', 0.66], ['BOS', 'Boston Harbour Union', 0.58], ['ATL', 'Atlanta Peach FC', 0.6], ['DEN', 'Denver Summit', 0.5]],
  china: [['SHD', 'Shanghai Dragons FC', 0.7], ['BJG', 'Beijing Guardians', 0.68], ['GZT', 'Guangzhou Tigers', 0.64], ['WUH', 'Wuhan River FC', 0.55], ['CDP', 'Chengdu Pandas', 0.52], ['HZL', 'Hangzhou Lakers FC', 0.58]],
  japan: [['TKB', 'Tokyo Bay FC', 0.7], ['OSK', 'Osaka Castle FC', 0.66], ['YKH', 'Yokohama Harbour', 0.62], ['KYO', 'Kyoto Lanterns', 0.55], ['SAP', 'Sapporo Snow FC', 0.5], ['FUK', 'Fukuoka Waves', 0.57]],
};
const MARGIN = 1.08; // (the bookmaker's: the three prices' implied chances add up to 108%)

export interface Fixture { id: string; home: [string, string]; away: [string, string]; p: [number, number, number]; odds: [number, number, number]; kickoff: number }
export type Pick = 0 | 1 | 2; // home, draw, away

/** The next three virtual matches (a new set every hour): two clubs each, the chances, the prices. */
export function fixtures(s: LifeState): Fixture[] {
  const hour = Math.floor(s.minutes / 60), clubs = CLUBS[hereCountry(s)] ?? CLUBS.nigeria;
  const r = new Rng((s.seed ^ Math.imul(hour + 17, 0x7feb352d)) >>> 0);
  const order = r.sample(clubs, 6);
  return [0, 1, 2].map((i) => {
    const h = order[i * 2], a = order[i * 2 + 1];
    const edge = (h[2] - a[2]) * 1.6 + 0.08; // (home advantage)
    const pd = 0.26 - Math.abs(edge) * 0.12, ph = Math.max(0.08, (1 - pd) / 2 + edge / 2), pa = Math.max(0.08, 1 - pd - ph);
    const t = ph + pd + pa, p: [number, number, number] = [ph / t, pd / t, pa / t];
    const odds = p.map((x) => Math.max(1.05, Math.round((1 / (x * MARGIN)) * 20) / 20)) as [number, number, number];
    return { id: `${hour}-${i}`, home: [h[0], h[1]], away: [a[0], a[1]], p, odds, kickoff: (hour + 1) * 60 };
  });
}

/** stake presets where you are (local money), and the share of your money that asks you first */
export const stakes = (s: LifeState) => [500, 2_000, 5_000, 20_000, 100_000].map((n) => localPrice(s, n, n >= 20_000 ? 1000 : 100));
export const bigStake = (s: LifeState, stake: number) => stake > s.money * 0.25;

export interface Betting { staked: number; returned: number; bets: number; biggest: number }
export const bettingOf = (s: LifeState): Betting => (s.betting ??= { staked: 0, returned: 0, bets: 0, biggest: 0 });

/** Put money down (it leaves your account now). False: you haven't got it. */
export function stake(s: LifeState, amount: number, what: string): boolean {
  if (amount <= 0 || !spend(s, amount, what)) return false;
  const b = bettingOf(s); b.staked += amount; b.bets++;
  return true;
}
function payout(s: LifeState, amount: number, what: string) {
  if (amount <= 0) return;
  earn(s, amount, what);
  const b = bettingOf(s); b.returned += amount; b.biggest = Math.max(b.biggest, amount);
}

/** Play a fixture out: the result by its chances, and a scoreline that fits it. Your pick paid at its price. */
export function playFixture(s: LifeState, f: Fixture, pick: Pick, amount: number): { result: Pick; score: [number, number]; won: boolean; paid: number; line: string } {
  const r = new Rng((s.seed ^ Math.imul(s.minutes + f.id.length * 7, 0x1b873593)) >>> 0);
  const x = r.next(), result: Pick = x < f.p[0] ? 0 : x < f.p[0] + f.p[1] ? 1 : 2;
  const goals = () => r.weighted([[0, 3], [1, 4], [2, 3], [3, 1.5], [4, 0.5]] as const);
  let hs = goals(), as = goals();
  if (result === 1) as = hs; else if (result === 0 && hs <= as) hs = as + 1 + (r.chance(0.3) ? 1 : 0); else if (result === 2 && as <= hs) as = hs + 1 + (r.chance(0.3) ? 1 : 0);
  const won = pick === result, paid = won ? Math.round(amount * f.odds[pick]) : 0;
  payout(s, paid, `Bet won: ${f.home[1]} v ${f.away[1]}`);
  const names = [f.home[1], 'the draw', f.away[1]];
  return { result, score: [hs, as], won, paid, line: won ? `${names[pick]} it is: ${money(s, paid)} back on your ${money(s, amount)}.` : `${f.home[1]} ${hs}–${as} ${f.away[1]}. Your slip goes in the bin.` };
}

// --- the machine: three reels, a printed paytable (it pays back about 92 in every 100 staked) ---------------
export const SYMBOLS = ['cherry', 'lemon', 'bell', 'bar', 'seven'] as const;
export type Sym = (typeof SYMBOLS)[number];
const REEL: [Sym, number][] = [['cherry', 7], ['lemon', 6], ['bell', 4], ['bar', 2], ['seven', 1]];
/** three of a kind pays this many times the stake; two cherries anywhere, your stake back (it pays back ~91 in 100) */
export const PAYS: Record<Sym, number> = { cherry: 4, lemon: 7, bell: 20, bar: 100, seven: 400 };
export function spin(s: LifeState, amount: number): { reels: [Sym, Sym, Sym]; win: number; line: string } | null {
  if (!stake(s, amount, 'The machine')) return null;
  const r = new Rng((s.seed ^ Math.imul(s.minutes * 31 + bettingOf(s).bets, 0x85ebca77)) >>> 0);
  const reels = [0, 1, 2].map(() => r.weighted(REEL)) as [Sym, Sym, Sym];
  const three = reels[0] === reels[1] && reels[1] === reels[2];
  const cherries = reels.filter((x) => x === 'cherry').length;
  const k = three ? PAYS[reels[0]] : cherries === 2 ? 1 : 0, win = Math.round(amount * k);
  payout(s, win, 'The machine pays out');
  return { reels, win, line: win ? `${three ? `Three ${reels[0]}s` : 'Two cherries: your stake back'}: ${money(s, win)}.` : 'Nothing. The reels hum and stop.' };
}

// --- scratch cards (the lottery shop): nine panels, three the same wins that prize ----------------------------
export function scratchCard(s: LifeState, price: number): { cells: number[]; prize: number } | null {
  if (!stake(s, price, 'A scratch card')) return null;
  const r = new Rng((s.seed ^ Math.imul(s.minutes * 17 + bettingOf(s).bets, 0xcc9e2d51)) >>> 0);
  const prizes = [0.5, 1, 2, 5, 20, 100].map((m) => Math.round(price * m));
  // (the card is decided when it's printed: about three in ten win something; it pays back ~60%, like a real lottery)
  const roll = r.next(), win = roll < 0.001 ? 5 : roll < 0.006 ? 4 : roll < 0.026 ? 3 : roll < 0.086 ? 2 : roll < 0.246 ? 1 : roll < 0.306 ? 0 : -1;
  const cells: number[] = [];
  if (win >= 0) cells.push(win, win, win);
  while (cells.length < 9) { const c = r.int(0, 5); if (cells.filter((x) => x === c).length < 2) cells.push(c); }
  for (let i = cells.length - 1; i > 0; i--) { const j = r.int(0, i); [cells[i], cells[j]] = [cells[j], cells[i]]; }
  const prize = win >= 0 ? prizes[win] : 0;
  payout(s, prize, 'Scratch card win');
  return { cells: cells.map((c) => prizes[c]), prize };
}
